import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18n/LanguageContext';
import { useHomeT } from '../i18n/strings';
import { portfolioItems } from '../data/portfolio';
import ProjectCard from '../components/ProjectCard';
import Badge from '../components/Badge';
import CasePanel from '../components/CasePanel';

const FILTER_KEYS = [
  ['All', 'filterAll'],
  ['Products', 'filterProducts'],
  ['Platforms', 'filterPlatforms'],
  ['Mobile', 'filterMobile'],
  ['Collaborations', 'filterCollab'],
  ['Education', 'filterEducation'],
  ['E-commerce', 'filterEcom'],
  ['In development', 'filterWip'],
];

const VALID_FILTERS = new Set(FILTER_KEYS.map(([k]) => k));

function readFilterFromUrl() {
  try {
    const raw = new URLSearchParams(window.location.search).get('filter');
    if (!raw) return 'All';
    // Accept exact labels and simple slug forms (products, in-development, e-commerce)
    if (VALID_FILTERS.has(raw)) return raw;
    const slug = raw.toLowerCase().replace(/[_+]/g, ' ').trim();
    for (const key of VALID_FILTERS) {
      if (key.toLowerCase() === slug) return key;
      if (key.toLowerCase().replace(/\s+/g, '-') === raw.toLowerCase()) return key;
    }
  } catch {
    /* ignore */
  }
  return 'All';
}

function writeUrlState({ filter, openKey }) {
  const params = new URLSearchParams(window.location.search);
  if (!filter || filter === 'All') params.delete('filter');
  else params.set('filter', filter);
  const qs = params.toString();
  const hash = openKey ? `#case-${openKey}` : '';
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${hash}`;
  const cur = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== cur) {
    window.history.replaceState(null, '', next);
  }
}

export default function WorkPage() {
  const { lang } = useLanguage();
  const t = useHomeT(lang);
  const [filter, setFilterState] = useState(() =>
    typeof window !== 'undefined' ? readFilterFromUrl() : 'All'
  );
  const [openKey, setOpenKey] = useState(null);

  const setFilter = useCallback((next) => {
    setFilterState(VALID_FILTERS.has(next) ? next : 'All');
  }, []);

  // Open case from #case-<key> on load and when hash changes (e.g. external link).
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash || '';
      const m = hash.match(/^#case-([a-z0-9-]+)$/i);
      if (m && portfolioItems.some((i) => i.key === m[1])) {
        setOpenKey(m[1]);
      } else if (!hash.startsWith('#case-')) {
        setOpenKey(null);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  // Keep ?filter= and #case-* in sync with state (preserve each other).
  useEffect(() => {
    writeUrlState({ filter, openKey });
  }, [filter, openKey]);

  // Respond to browser back/forward for query changes.
  useEffect(() => {
    const onPop = () => {
      setFilterState(readFilterFromUrl());
      const hash = window.location.hash || '';
      const m = hash.match(/^#case-([a-z0-9-]+)$/i);
      setOpenKey(m && portfolioItems.some((i) => i.key === m[1]) ? m[1] : null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const items = useMemo(() => {
    if (filter === 'All') return portfolioItems;
    return portfolioItems.filter((i) => i.cats.includes(filter));
  }, [filter]);

  const showUk = filter === 'All' || filter === 'Platforms' || filter === 'Education';
  const openItem = portfolioItems.find((i) => i.key === openKey) || null;

  const workDesc =
    lang === 'lv'
      ? 'GDWD atlasītie darbi — produkti, platformas, mobilās lietotnes un sadarbības.'
      : t.workLead;

  return (
    <>
      <Helmet>
        <title>{lang === 'lv' ? 'GDWD — Darbi' : 'GDWD — Work'}</title>
        <meta name="description" content={workDesc} />
      </Helmet>

      <header className="gdwd-work-header">
        <div className="gdwd-container">
          <div className="gdwd-kicker">{`// ${t.workKicker}`}</div>
          <h1 className="gdwd-h1" style={{ fontSize: 'clamp(40px, 5vw, 56px)' }}>
            {t.workTitle}
            <span>.</span>
          </h1>
          <p className="gdwd-lead" style={{ maxWidth: 640 }}>
            {t.workLead}
          </p>
          <div className="gdwd-filters" role="tablist" aria-label="Filters">
            {FILTER_KEYS.map(([key, tKey]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                className={filter === key ? 'is-active' : ''}
                onClick={() => setFilter(key)}
              >
                {t[tKey]}
              </button>
            ))}
          </div>
        </div>
      </header>

      <section className="gdwd-section" style={{ paddingTop: 0 }}>
        <div className="gdwd-container">
          {showUk ? (
            <div className="gdwd-uk" style={{ marginBottom: 28, marginTop: 0 }}>
              <div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  <Badge kind="Platform">{t.badgePlatform}</Badge>
                  <span className="gdwd-note" style={{ color: 'var(--console-muted)' }}>
                    {t.ukTag}
                  </span>
                </div>
                <h3>{t.ukTitle}</h3>
                <p>{t.ukBlurb}</p>
              </div>
              <div>
                <div className="gdwd-uk__logos">
                  <div className="gdwd-uk__logo">
                    <img src="/assets/logos/lsst.png" alt="LSST" />
                  </div>
                  <div className="gdwd-uk__logo">
                    <img src="/assets/logos/fsb.png" alt="FSB" />
                  </div>
                  <div className="gdwd-uk__logo">
                    <img
                      src="/assets/logos/ukbc-web-logo.png"
                      alt="UKBC"
                      onError={(e) => {
                        e.currentTarget.src = '/assets/logos/ukbc.png';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {filter === 'All' ? (
            <>
              {[
                // Products includes Trukka + FieldLedger. Mobile group = pure mobile apps (Attendance).
                // In development = note/cats, not a separate kind for Trukka/FieldLedger.
                ['Products', t.filterProducts, (i) => i.kind === 'Product'],
                ['Platforms', t.filterPlatforms, (i) => i.kind === 'Platform'],
                ['Mobile', t.filterMobile, (i) => i.kind === 'Mobile'],
                ['Collaborations', t.filterCollab, (i) => i.kind === 'Collaboration'],
                [
                  'In development',
                  t.filterWip,
                  (i) => (i.cats || []).includes('In development') || i.kind === 'Wip',
                ],
              ].map(([key, label, pred]) => {
                const group = portfolioItems.filter(pred);
                if (!group.length) return null;
                return (
                  <div key={key}>
                    <div className="gdwd-work-group" id={`group-${key.toLowerCase().replace(/\s+/g, '-')}`}>
                      {label}
                    </div>
                    <div className="gdwd-grid-3">
                      {group.map((item) => (
                        <ProjectCard key={item.key} item={item} onOpen={setOpenKey} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="gdwd-grid-3">
              {items.map((item) => (
                <ProjectCard key={item.key} item={item} onOpen={setOpenKey} />
              ))}
            </div>
          )}
        </div>
      </section>

      <CasePanel item={openItem} onClose={() => setOpenKey(null)} />
    </>
  );
}
